import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '../../services/auth.service';
import { Subject, takeUntil, interval } from 'rxjs';

/**
 * TokenExpiryDialogComponent
 *
 * Warning dialog shown 30 seconds before token expiry.
 * Allows user to extend session or logout.
 * Auto-logout if no action taken within countdown.
 *
 * Features:
 * - Countdown timer display
 * - Extend session button (refresh token)
 * - Logout button
 * - Auto-logout on countdown completion
 * - Can be triggered programmatically
 */
@Component({
    selector: 'myabvv-token-expiry-dialog',
    standalone: true,
    templateUrl: './token-expiry-dialog.component.html',
    styleUrls: ['./token-expiry-dialog.component.css'],
    imports: [TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TokenExpiryDialogComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private router = inject(Router);

  isVisible = signal(false);
  secondsRemaining = signal(30);
  isExtending = signal(false);

  formattedTime = computed(() => {
    const minutes = Math.floor(this.secondsRemaining() / 60);
    const seconds = this.secondsRemaining() % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  });

  progressPercentage = computed(() => (this.secondsRemaining() / 30) * 100);

  urgencyClass = computed(() => {
    if (this.secondsRemaining() <= 10) {
      return 'critical';
    } else if (this.secondsRemaining() <= 20) {
      return 'warning';
    }
    return 'normal';
  });

  private destroy$ = new Subject<void>();
  private countdownInterval: ReturnType<typeof setInterval> | null;

  ngOnInit(): void {
    // Subscribe to token expiry warnings from AuthService
    // (This would need to be implemented in AuthService as an Observable)
    this.startMonitoring();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopCountdown();
  }

  /**
   * Start monitoring token expiry
   * This is a placeholder - in production, AuthService would emit events
   */
  private startMonitoring(): void {
    // Check token expiry every 5 seconds
    interval(5000)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.checkTokenExpiry(),
        error: err => console.error('[TokenExpiryDialogComponent] Monitoring interval error:', err)
      });
  }

  /**
   * Check if token is about to expire
   * Show dialog if expiry is within 30 seconds
   */
  private checkTokenExpiry(): void {
    if (this.isVisible()) {
      return;
    }

    const expiry = this.authService.getTokenExpiration();
    if (!expiry) {
      return;
    }

    const now = Date.now();
    const timeUntilExpiry = expiry - now;
    const secondsUntilExpiry = Math.floor(timeUntilExpiry / 1000);

    // Show dialog if token expires in 30 seconds or less
    if (secondsUntilExpiry <= 30 && secondsUntilExpiry > 0) {
      this.show(secondsUntilExpiry);
    }
  }

  /**
   * Show the dialog with countdown
   */
  public show(secondsRemaining: number = 30): void {
    this.isVisible.set(true);
    this.secondsRemaining.set(secondsRemaining);
    this.startCountdown();
  }

  /**
   * Hide the dialog
   */
  public hide(): void {
    this.isVisible.set(false);
    this.stopCountdown();
  }

  /**
   * Start countdown timer
   */
  private startCountdown(): void {
    this.stopCountdown(); // Clear any existing countdown

    this.countdownInterval = setInterval(() => {
      this.secondsRemaining.update(v => v - 1);

      if (this.secondsRemaining() <= 0) {
        this.stopCountdown();
        this.onLogout();
      }
    }, 1000);
  }

  /**
   * Stop countdown timer
   */
  private stopCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  /**
   * Handle extend session button click
   * Refreshes the access token
   */
  async onExtendSession(): Promise<void> {
    try {
      this.isExtending.set(true);
      const success = await this.authService.refreshToken();
      if (success) {
        this.hide();
      }
      // If not successful, let the countdown continue naturally
    } catch (error) {
      console.error('[TokenExpiryDialogComponent] Token refresh error:', error);
      // Do not auto-logout on error — let the user decide via the logout button
    } finally {
      this.isExtending.set(false);
    }
  }

  /**
   * Handle logout button click
   * Ends the session and redirects to login
   */
  async onLogout(): Promise<void> {
    this.hide();
    await this.authService.logout();
    this.router.navigate(['/auth/landing']);
  }
}
